import {
  Box,
  CircularProgress,
  Stack,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  GetDisplayCoolerProductStatsQuery,
  SortDirection,
} from 'graphql/generated-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import TrendIndicator from '@/components/common/TrendIndicator'
import {
  COOLER_PRODUCTS_COLUMN_IDS,
  TableColumnVisibilityMenu,
  useCoolerProductsColumnDefinitions,
  useTableColumnVisibility,
} from '@/features/table-column-visibility'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import DeliciousTable, { HeadCell } from '@/pages/components/DeliciousTable'

import useProductsByUpc from '../hooks/useProductsByUpc'
import {
  formatCurrencyOrDash,
  formatDecimalOrDash,
  formatMarginValue,
  formatNumberOrDash,
} from '../utils/cooler-detail-formatters'

type CoolerProductStat =
  GetDisplayCoolerProductStatsQuery['displayCoolerProductStats']['results'][number]
type DisplayCoolerProductStatsOrdering = NonNullable<
  import('graphql/generated-types').GetDisplayCoolerProductStatsQueryVariables['ordering']
>

const HEADER_TO_MANAGED_COLUMN_ID: Partial<
  Record<keyof DisplayCoolerProductStatsOrdering, string>
> = {
  revenuePerFacing: 'revenue_per_facing',
  averageVolumePerDay: 'average_volume_per_day',
  daysSupply: 'days_supply',
}

const MANAGED_COLUMN_ID_TO_ORDER_BY: Record<
  string,
  keyof DisplayCoolerProductStatsOrdering
> = {
  revenue_per_facing: 'revenuePerFacing',
  average_volume_per_day: 'averageVolumePerDay',
  days_supply: 'daysSupply',
}

const toManagedColumnId = (
  headerId: keyof DisplayCoolerProductStatsOrdering | string,
) =>
  HEADER_TO_MANAGED_COLUMN_ID[
    headerId as keyof DisplayCoolerProductStatsOrdering
  ] || headerId

const toOrderBy = (
  managedColumnId: string,
): keyof DisplayCoolerProductStatsOrdering =>
  MANAGED_COLUMN_ID_TO_ORDER_BY[managedColumnId] ||
  (managedColumnId as keyof DisplayCoolerProductStatsOrdering)

type Props = {
  rows: CoolerProductStat[]
  totalCount: number
  loading: boolean
  error?: string
  order: SortDirection
  orderBy: keyof DisplayCoolerProductStatsOrdering
  setOrder: (order: SortDirection) => void
  setOrderBy: (orderBy: keyof DisplayCoolerProductStatsOrdering) => void
  page: number
  rowsPerPage: number
  setPage: (page: number) => void
  setRowsPerPage: (rowsPerPage: number) => void
  handleRequestSort: (sortKey: keyof DisplayCoolerProductStatsOrdering) => void
  onRowHoverChange?: (upc: string | null) => void
  t: (key: string, params?: Record<string, any>) => string
}

const columnWidthById: Record<string, number> = {
  revenue: 116,
  margin: 88,
  volume: 80,
  facings: 80,
  revenue_per_facing: 96,
  average_volume_per_day: 110,
  days_supply: 96,
}
const MAX_STICKY_COLUMN_WIDTH = 220

const CoolerProductStatsTable: React.FC<Props> = ({
  rows,
  totalCount,
  loading,
  error,
  order,
  orderBy,
  setOrder,
  setOrderBy,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  handleRequestSort,
  onRowHoverChange,
  t,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [horizontalScroll, setHorizontalScroll] = useState(0)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const [tableContainerWidth, setTableContainerWidth] = useState(0)
  const { hasOwnerRole } = useLoggedInUser()
  const { loading: productsLoading, productImageByUpc } = useProductsByUpc(
    rows.map(row => row.upc),
  )
  const columnDefinitions = useCoolerProductsColumnDefinitions()
  const {
    visibleColumnIds,
    effectiveHiddenSet,
    userHiddenSet,
    tenantHiddenSet,
    hideForUser,
    showForUser,
    hideForTenant,
    showForTenant,
    error: columnVisibilityError,
    mutationLoading,
  } = useTableColumnVisibility(
    'cooler_products_list',
    COOLER_PRODUCTS_COLUMN_IDS,
    {
      updateErrorMessage: t('tableColumns.updateError'),
      permissionErrorMessage: t('tableColumns.permissionError'),
    },
  )
  const visibleColumnIdSet = useMemo(
    () => new Set<string>(visibleColumnIds),
    [visibleColumnIds],
  )

  const headers = [
    {
      id: 'name',
      label: t('common.product'),
      alignment: 'left',
      sortKey: 'name',
    },
    {
      id: 'revenue',
      label: t('common.revenue'),
      alignment: 'left',
      sortKey: 'revenue',
    },
    {
      id: 'margin',
      label: t('common.margin'),
      alignment: 'left',
      sortKey: 'margin',
    },
    {
      id: 'volume',
      label: t('common.volume'),
      alignment: 'left',
      sortKey: 'volume',
    },
    {
      id: 'facings',
      label: t('common.facings'),
      alignment: 'left',
      sortKey: 'facings',
    },
    {
      id: 'revenuePerFacing',
      label: t('coolerProductStatsTable.columns.revenuePerFacing'),
      alignment: 'left',
      sortKey: 'revenuePerFacing',
    },
    {
      id: 'averageVolumePerDay',
      label: t('coolerProductStatsTable.columns.averageVolumePerDay'),
      alignment: 'left',
      sortKey: 'averageVolumePerDay',
    },
    {
      id: 'daysSupply',
      label: t('coolerProductStatsTable.columns.daysSupply'),
      alignment: 'left',
      sortKey: 'daysSupply',
    },
  ] satisfies HeadCell<DisplayCoolerProductStatsOrdering>[]

  const visibleHeaders = headers.filter(header => {
    if (header.id === 'name') {
      return true
    }

    return visibleColumnIdSet.has(toManagedColumnId(header.id))
  })

  const managedColumnsWidth = visibleColumnIds.reduce(
    (sum: number, columnId: string) => sum + (columnWidthById[columnId] || 80),
    0,
  )

  const stickyColumnWidth = useMemo(() => {
    const maxWidth = MAX_STICKY_COLUMN_WIDTH
    const minWidth = isMobile ? 150 : 160
    const preferredTableWidth = maxWidth + managedColumnsWidth
    const overflowWidth = Math.max(preferredTableWidth - tableContainerWidth, 0)
    const overflowProgress = Math.min(
      overflowWidth / (maxWidth - minWidth || 1),
      1,
    )
    const scrollProgress = isMobile ? Math.min(horizontalScroll / 96, 1) : 0
    const progress = Math.max(overflowProgress, scrollProgress)

    return maxWidth - (maxWidth - minWidth) * progress
  }, [horizontalScroll, isMobile, managedColumnsWidth, tableContainerWidth])

  const shouldUseStickyNameColumn = true

  useEffect(() => {
    if (!tableContainerRef.current) return

    const element = tableContainerRef.current
    const updateWidth = () => setTableContainerWidth(element.clientWidth)
    updateWidth()

    const observer = new ResizeObserver(() => updateWidth())
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  const getCellSx = (width: number) => ({
    py: 1,
    minWidth: width,
    whiteSpace: 'nowrap',
  })

  const renderRow = (row: CoolerProductStat, index: number) => {
    const imageUrl =
      productImageByUpc[row.upc]?.planogramImageUrl ||
      productImageByUpc[row.upc]?.marketingImageUrl ||
      '/static/img/products/unknown-product-planogram-facing.png'

    return (
      <TableRow
        key={`${row.upc}-${index}`}
        hover
        onMouseEnter={() => onRowHoverChange?.(row.upc)}
        onMouseLeave={() => onRowHoverChange?.(null)}
      >
        <TableCell
          sx={{
            py: 1,
            position: shouldUseStickyNameColumn ? 'sticky' : 'static',
            left: 0,
            zIndex: shouldUseStickyNameColumn ? 2 : 1,
            backgroundColor: 'background.paper',
            minWidth: stickyColumnWidth,
            width: stickyColumnWidth,
            maxWidth: stickyColumnWidth,
            boxShadow: shouldUseStickyNameColumn
              ? '8px 0 12px -12px rgba(0,0,0,0.35)'
              : 'none',
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 30,
                height: 42,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <img
                src={imageUrl}
                alt={row.name || row.upc}
                style={{
                  width: 30,
                  height: 42,
                  objectFit: 'contain',
                  opacity:
                    productsLoading && !productImageByUpc[row.upc] ? 0.55 : 1,
                }}
              />
              {productsLoading && !productImageByUpc[row.upc] && (
                <CircularProgress
                  size={14}
                  sx={{
                    position: 'absolute',
                  }}
                />
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {row.name || row.upc}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.upc}
              </Typography>
            </Box>
          </Stack>
        </TableCell>
        {visibleColumnIdSet.has('revenue') && (
          <TableCell align="left" sx={getCellSx(116)}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">
                {formatCurrencyOrDash(row.revenue)}
              </Typography>
              {row.revenueTrend !== null && row.revenueTrend !== undefined && (
                <TrendIndicator value={row.revenueTrend} suffix="%" />
              )}
            </Stack>
          </TableCell>
        )}
        {visibleColumnIdSet.has('margin') && (
          <TableCell align="left" sx={getCellSx(88)}>
            <Typography variant="body2">
              {formatMarginValue(row.margin, t('coolersTable.values.unknown'))}
            </Typography>
          </TableCell>
        )}
        {visibleColumnIdSet.has('volume') && (
          <TableCell align="left" sx={getCellSx(80)}>
            <Typography variant="body2">
              {formatNumberOrDash(row.volume)}
            </Typography>
          </TableCell>
        )}
        {visibleColumnIdSet.has('facings') && (
          <TableCell align="left" sx={getCellSx(80)}>
            <Typography variant="body2">
              {formatNumberOrDash(row.facings)}
            </Typography>
          </TableCell>
        )}
        {visibleColumnIdSet.has('revenue_per_facing') && (
          <TableCell align="left" sx={getCellSx(96)}>
            <Typography variant="body2">
              {formatCurrencyOrDash(row.revenuePerFacing)}
            </Typography>
          </TableCell>
        )}
        {visibleColumnIdSet.has('average_volume_per_day') && (
          <TableCell align="left" sx={getCellSx(110)}>
            <Typography variant="body2">
              {formatDecimalOrDash(row.averageVolumePerDay)}
            </Typography>
          </TableCell>
        )}
        {visibleColumnIdSet.has('days_supply') && (
          <TableCell align="left" sx={getCellSx(96)}>
            <Typography variant="body2">
              {formatDecimalOrDash(row.daysSupply)}
            </Typography>
          </TableCell>
        )}
      </TableRow>
    )
  }

  useEffect(() => {
    const currentManagedColumnId = toManagedColumnId(orderBy)

    if (currentManagedColumnId === 'name') {
      return
    }

    if (visibleColumnIdSet.has(currentManagedColumnId)) {
      return
    }

    if (visibleColumnIdSet.has('revenue')) {
      setOrderBy('revenue')
      setOrder(SortDirection.DESC)
      return
    }

    const fallbackColumnId = visibleColumnIds[0]
    const fallbackOrderBy = fallbackColumnId
      ? toOrderBy(fallbackColumnId)
      : undefined

    if (fallbackOrderBy) {
      setOrderBy(fallbackOrderBy)
      setOrder(SortDirection.DESC)
    }
  }, [orderBy, setOrder, setOrderBy, visibleColumnIdSet, visibleColumnIds])

  return (
    <DeliciousTable<CoolerProductStat, DisplayCoolerProductStatsOrdering>
      title={t('common.products')}
      size="small"
      data={rows}
      headCells={visibleHeaders}
      renderRow={renderRow}
      loading={loading}
      error={error}
      totalCount={totalCount}
      order={order === SortDirection.ASC ? 'asc' : 'desc'}
      orderBy={orderBy}
      handleRequestSort={handleRequestSort}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={setPage}
      onRowsPerPageChange={setRowsPerPage}
      noDataMessage={t('coolersTable.noData')}
      stickyHeader
      paperSx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      tableContainerSx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'auto',
      }}
      RightHeaderComponent={
        <TableColumnVisibilityMenu
          columns={columnDefinitions}
          effectiveHiddenSet={effectiveHiddenSet}
          userHiddenSet={userHiddenSet}
          tenantHiddenSet={tenantHiddenSet}
          isAdmin={hasOwnerRole}
          disabled={Boolean(columnVisibilityError)}
          loading={mutationLoading}
          buttonLabel={t('tableColumns.button')}
          hiddenForEveryoneLabel={t('tableColumns.hiddenForEveryone')}
          managedByTenantLabel={t('tableColumns.managedByTenant')}
          hiddenForYouLabel={t('tableColumns.hiddenForYou')}
          hideForYouLabel={t('tableColumns.hideForYou')}
          showForYouLabel={t('tableColumns.showForYou')}
          hideForEveryoneLabel={t('tableColumns.hideForEveryone')}
          showForEveryoneLabel={t('tableColumns.showForEveryone')}
          onHideForUser={hideForUser}
          onShowForUser={showForUser}
          onHideForTenant={hideForTenant}
          onShowForTenant={showForTenant}
        />
      }
      tableSx={
        shouldUseStickyNameColumn
          ? {
              minWidth: stickyColumnWidth + managedColumnsWidth,
            }
          : undefined
      }
      getHeadCellSx={headCell =>
        shouldUseStickyNameColumn
          ? headCell.id === 'name'
            ? {
                position: 'sticky',
                left: 0,
                zIndex: 3,
                backgroundColor: 'background.paper',
                minWidth: stickyColumnWidth,
                maxWidth: stickyColumnWidth,
                boxShadow: '8px 0 12px -12px rgba(0,0,0,0.35)',
              }
            : {
                minWidth:
                  headCell.id === 'revenue'
                    ? 116
                    : headCell.id === 'revenuePerFacing'
                      ? 96
                      : 80,
                whiteSpace: 'nowrap',
              }
          : undefined
      }
      tableContainerProps={{
        ref: tableContainerRef,
        onScroll: event => {
          const target = event.currentTarget
          setHorizontalScroll(target.scrollLeft)
        },
      }}
    />
  )
}

export default CoolerProductStatsTable
